/**
 * RolloverDialog Component
 * Standalone dialog for contract rollover with per-investor capital allocation.
 * Supports both single-investor and multi-investor contracts.
 * Creates a pending_changes entry for approval workflow.
 * Preview opens ScheduleImpactPreviewModal with projected schedule.
 */

import React, { useState, useEffect } from 'react';
import {
  TextField,
  Grid,
  Typography,
  Box,
  Paper,
  Divider,
  CircularProgress,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import { format, addDays, addMonths, addYears } from 'date-fns';
import { apiPost, apiGet } from '../../utils/apiClient';
import { formatNumber } from '../../utils/validation';
import ContractActionDialogBase from './ContractActionDialogBase';
import ScheduleImpactPreviewModal from './ScheduleImpactPreviewModal';

// Format number with space thousand separator and period decimal
const formatWithSpaces = (value) => {
  const num = parseFloat(value);
  if (isNaN(num)) return '0.00';
  return num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

// Currency input: formatted when blurred, raw number when focused
function CurrencyInput({ value, onChange, label, helperText, disabled, sx, ...props }) {
  const [focused, setFocused] = useState(false);
  const showFormatted = !focused || disabled;
  const rawValue = (() => {
    const num = parseFloat(value);
    return isNaN(num) ? '' : String(num);
  })();

  return (
    <TextField
      {...props}
      label={label}
      fullWidth
      value={showFormatted ? formatWithSpaces(value) : rawValue}
      onChange={disabled ? undefined : (e) => onChange(e.target.value.replace(/\s/g, ''))}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      type={showFormatted ? 'text' : 'number'}
      inputProps={showFormatted ? {} : { step: 0.01 }}
      InputLabelProps={{ shrink: true }}
      helperText={helperText}
      disabled={disabled}
      sx={sx}
    />
  );
}

const RolloverDialog = ({ open, onClose, contract, contractId: explicitContractId, onSubmitSuccess }) => {
  const contractId = explicitContractId || contract?.id;

  // Rollover form state
  const [rolloverData, setRolloverData] = useState({
    investment_term: 'Years',
    investment_term_value: '',
    new_maturity_date: '',
    new_interest_rate: '',
    interest_paid_frequency_id: '',
    capital_paid_frequency_id: '',
    calculation_profile_id: '',
  });
  const [investorAllocations, setInvestorAllocations] = useState([]);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [frequencies, setFrequencies] = useState([]);
  const [calculationProfiles, setCalculationProfiles] = useState([]);
  const [loadingInvestors, setLoadingInvestors] = useState(false);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setRolloverData({
        investment_term: contract?.investment_term || 'Years',
        investment_term_value: '',
        new_maturity_date: '',
        new_interest_rate: '',
        interest_paid_frequency_id: contract?.interest_paid_frequency_id || '',
        capital_paid_frequency_id: contract?.capital_paid_frequency_id || '',
        calculation_profile_id: contract?.calculation_profile_id || '',
      });
      setInvestorAllocations([]);
      setReason('');
      setError(null);
      setPreviewData(null);
      setPreviewModalOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- contract fields used for initial values only when dialog opens
  }, [open]);

  // Load frequencies, sync defaults, and fetch investors when dialog opens
  useEffect(() => {
    if (open) {
      if (frequencies.length === 0) {
        apiGet('/refdata/frequencies').then(res => {
          if (res?.success) setFrequencies(res.data || []);
        }).catch(() => {});
      }
      if (calculationProfiles.length === 0) {
        apiGet('/refdata/calculation-profiles').then(res => {
          if (res?.success) setCalculationProfiles(res.data || []);
        }).catch(() => {});
      }
      // Sync frequency defaults from contract when dialog opens
      if (contract) {
        setRolloverData(prev => ({
          ...prev,
          investment_term: prev.investment_term || contract.investment_term || 'Years',
          interest_paid_frequency_id: prev.interest_paid_frequency_id || contract.interest_paid_frequency_id || '',
          capital_paid_frequency_id: prev.capital_paid_frequency_id || contract.capital_paid_frequency_id || '',
          calculation_profile_id: prev.calculation_profile_id || contract.calculation_profile_id || '',
        }));
      }
      // Fetch investors for per-investor allocation
      if (contractId && investorAllocations.length === 0) {
        setLoadingInvestors(true);
        apiGet(`/contracts/${contractId}/investors`).then(res => {
          const investors = res?.data?.investors || res?.data || [];
          if (investors.length > 0) {
            setInvestorAllocations(investors.map(inv => ({
              investor_id: inv.investor_id,
              investor_name: inv.investor_name,
              original_capital: parseFloat(inv.capital_amount) || 0,
              rolled_amount: parseFloat(inv.capital_amount) || 0,
              payout_amount: 0,
              unallocated_amount: 0,
              wht_rate: parseFloat(inv.wht_rate) || 0,
              wht_gross_up: inv.wht_gross_up || false,
              new_funds_amount: 0,
            })));
          } else {
            // Single investor - build from contract data
            setInvestorAllocations([{
              investor_id: contract?.investor_id,
              investor_name: contract?.investor_name || 'Investor',
              original_capital: parseFloat(contract?.capital_amount) || 0,
              rolled_amount: parseFloat(contract?.capital_amount) || 0,
              payout_amount: 0,
              unallocated_amount: 0,
              wht_rate: 0,
              wht_gross_up: false,
            }]);
          }
        }).catch(() => {
          // Fallback to single investor from contract
          setInvestorAllocations([{
            investor_id: contract?.investor_id,
            investor_name: contract?.investor_name || 'Investor',
            original_capital: parseFloat(contract?.capital_amount) || 0,
            rolled_amount: parseFloat(contract?.capital_amount) || 0,
            payout_amount: 0,
            unallocated_amount: 0,
            wht_rate: 0,
            wht_gross_up: false,
            new_funds_amount: 0,
          }]);
        }).finally(() => setLoadingInvestors(false));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- .length checks are intentional one-time load guards
  }, [open, contract, contractId, frequencies.length]);

  // Submit rollover for approval
  const handleSubmit = async ({ proof_document_ids }) => {
    setSubmitting(true);
    setError(null);
    try {
      const response = await apiPost(`/contracts/${contractId}/rollover`, {
        new_maturity_date: rolloverData.new_maturity_date,
        new_interest_rate: rolloverData.new_interest_rate ? parseFloat(rolloverData.new_interest_rate) : null,
        investor_allocations: investorAllocations.map(inv => ({
          investor_id: inv.investor_id,
          original_capital: inv.original_capital,
          rolled_amount: parseFloat(inv.rolled_amount) || 0,
          payout_amount: parseFloat(inv.payout_amount) || 0,
          unallocated_amount: parseFloat(inv.unallocated_amount) || 0,
          new_funds_amount: parseFloat(inv.new_funds_amount) || 0,
        })),
        interest_paid_frequency_id: rolloverData.interest_paid_frequency_id || null,
        capital_paid_frequency_id: rolloverData.capital_paid_frequency_id || null,
        investment_term: rolloverData.investment_term,
        investment_term_value: rolloverData.investment_term_value ? parseInt(rolloverData.investment_term_value) : null,
        calculation_profile_id: rolloverData.calculation_profile_id || null,
        reason,
        proof_document_ids,
      });
      if (response?.success) {
        onClose();
        if (onSubmitSuccess) onSubmitSuccess();
      } else {
        setError(response?.message || 'Failed to submit rollover request');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to submit rollover request');
    } finally {
      setSubmitting(false);
    }
  };

  // Preview schedule impact
  const handlePreview = async () => {
    if (!rolloverData.new_maturity_date) return;
    setPreviewLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        new_maturity_date: rolloverData.new_maturity_date,
        is_rollover: 'true',
      });
      if (rolloverData.new_interest_rate) params.append('new_interest_rate', rolloverData.new_interest_rate);
      // Compute capital adjustment from investor allocations (rolled + new funds)
      const totalNewCapital = investorAllocations.reduce((sum, inv) => sum + (parseFloat(inv.rolled_amount) || 0) + (parseFloat(inv.new_funds_amount) || 0), 0);
      const capitalAdjustment = totalNewCapital - parseFloat(contract?.capital_amount || 0);
      if (capitalAdjustment !== 0) params.append('capital_adjustment', capitalAdjustment);
      if (rolloverData.interest_paid_frequency_id) params.append('interest_paid_frequency_id', rolloverData.interest_paid_frequency_id);
      if (rolloverData.capital_paid_frequency_id) params.append('capital_paid_frequency_id', rolloverData.capital_paid_frequency_id);
      const response = await apiGet(`/amendments/contract/${contractId}/extension-preview?${params}`);
      if (response?.success) {
        setPreviewData(response.data);
        setPreviewModalOpen(true);
      }
    } catch (err) {
      setError('Failed to generate preview: ' + (err.response?.data?.message || err.message));
    } finally {
      setPreviewLoading(false);
    }
  };

  // Handler for investor allocation changes
  // rolled + payout + unallocated must equal original_capital
  // Unallocated (roll to new contract) is always auto-calculated as the remainder
  // new_funds is independent (additional fresh capital on top)
  const handleAllocationChange = (investorId, field, value) => {
    setInvestorAllocations(prev => prev.map(inv => {
      if (inv.investor_id !== investorId) return inv;
      const numVal = Math.max(parseFloat(value) || 0, 0);
      const updated = { ...inv, [field]: numVal };

      if (field === 'new_funds_amount') return updated;

      const rolled = field === 'rolled_amount' ? numVal : (parseFloat(inv.rolled_amount) || 0);
      const payout = field === 'payout_amount' ? numVal : (parseFloat(inv.payout_amount) || 0);
      updated.unallocated_amount = Math.max(inv.original_capital - rolled - payout, 0);
      return updated;
    }));
  };

  // Totals for investor allocations
  const allocationTotals = investorAllocations.reduce((t, inv) => ({
    original: t.original + (inv.original_capital || 0),
    rolled: t.rolled + (parseFloat(inv.rolled_amount) || 0),
    payout: t.payout + (parseFloat(inv.payout_amount) || 0),
    unallocated: t.unallocated + (parseFloat(inv.unallocated_amount) || 0),
    newFunds: t.newFunds + (parseFloat(inv.new_funds_amount) || 0),
  }), { original: 0, rolled: 0, payout: 0, unallocated: 0, newFunds: 0 });

  // Check if any investor's allocation exceeds their original capital
  const hasOverAllocation = investorAllocations.some(inv => {
    const sum = (parseFloat(inv.rolled_amount) || 0) + (parseFloat(inv.payout_amount) || 0) + (parseFloat(inv.unallocated_amount) || 0);
    return sum - inv.original_capital > 0.01;
  });

  const currencyCode = contract?.currency_code || 'ZAR';

  // Resolve selected calculation profile for detail display
  const selectedProfile = rolloverData.calculation_profile_id
    ? calculationProfiles.find(p => p.id === parseInt(rolloverData.calculation_profile_id))
    : null;

  return (
    <>
      <ContractActionDialogBase
        open={open}
        onClose={onClose}
        contract={contract}
        workflowCode="contract_rollover"
        title="Contract Rollover"
        description="Extend this contract with new terms. The amortization schedule will be regenerated upon approval."
        submitLabel="Submit for Approval"
        submitColor="warning"
        isValid={!!rolloverData.new_maturity_date && !hasOverAllocation && investorAllocations.some(inv => (parseFloat(inv.rolled_amount) || 0) > 0)}
        submitting={submitting}
        error={error}
        onClearError={() => setError(null)}
        onSubmit={handleSubmit}
        onPreview={handlePreview}
        showPreview={true}
        previewLoading={previewLoading}
        reason={reason}
        onReasonChange={setReason}
        reasonLabel="Reason (Required)"
        reasonPlaceholder="Provide a reason for the rollover. This will be reviewed by an approver."
      >
        <Grid container spacing={2}>
          <Grid size={3}>
            <TextField
              label="Term Unit"
              select
              fullWidth
              value={rolloverData.investment_term}
              onChange={(e) => {
                const unit = e.target.value;
                setRolloverData(prev => {
                  const updated = { ...prev, investment_term: unit };
                  // Recalculate maturity if term value exists
                  if (prev.investment_term_value && contract?.maturity_date) {
                    const startDate = new Date(contract.maturity_date);
                    const termVal = parseInt(prev.investment_term_value);
                    if (!isNaN(termVal) && termVal > 0) {
                      const maturity = unit === 'Days' ? addDays(startDate, termVal)
                        : unit === 'Months' ? addMonths(startDate, termVal)
                        : addYears(startDate, termVal);
                      updated.new_maturity_date = format(maturity, 'yyyy-MM-dd');
                    }
                  }
                  return updated;
                });
              }}
              InputLabelProps={{ shrink: true }}
            >
              <MenuItem value="Days">Days</MenuItem>
              <MenuItem value="Months">Months</MenuItem>
              <MenuItem value="Years">Years</MenuItem>
            </TextField>
          </Grid>
          <Grid size={3}>
            <TextField
              label="Term Value *"
              type="number"
              fullWidth
              value={rolloverData.investment_term_value}
              onChange={(e) => {
                const termVal = e.target.value;
                setRolloverData(prev => {
                  const updated = { ...prev, investment_term_value: termVal };
                  // Auto-calculate maturity from old contract's maturity + term
                  if (termVal && contract?.maturity_date) {
                    const startDate = new Date(contract.maturity_date);
                    const num = parseInt(termVal);
                    if (!isNaN(num) && num > 0) {
                      const maturity = prev.investment_term === 'Days' ? addDays(startDate, num)
                        : prev.investment_term === 'Months' ? addMonths(startDate, num)
                        : addYears(startDate, num);
                      updated.new_maturity_date = format(maturity, 'yyyy-MM-dd');
                    }
                  } else {
                    updated.new_maturity_date = '';
                  }
                  return updated;
                });
              }}
              inputProps={{ step: 1, min: 1 }}
              InputLabelProps={{ shrink: true }}
              placeholder="e.g., 2"
            />
          </Grid>
          <Grid size={3}>
            <TextField
              label="New Maturity Date"
              type="date"
              fullWidth
              value={rolloverData.new_maturity_date}
              onChange={(e) => setRolloverData(prev => ({ ...prev, new_maturity_date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              helperText="Auto-calculated or manual"
            />
          </Grid>
          <Grid size={3}>
            <TextField
              label="New Interest Rate (%)"
              type="number"
              fullWidth
              value={rolloverData.new_interest_rate}
              onChange={(e) => setRolloverData(prev => ({ ...prev, new_interest_rate: e.target.value }))}
              helperText="Blank = keep current"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          {/* Payment Frequencies */}
          <Grid size={6}>
            <TextField
              label="Interest Payment Frequency"
              select
              fullWidth
              value={rolloverData.interest_paid_frequency_id}
              onChange={(e) => setRolloverData(prev => ({ ...prev, interest_paid_frequency_id: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              helperText={`Current: ${contract?.interest_paid_frequency || '-'}`}
            >
              {frequencies.map((f) => (
                <MenuItem key={f.id} value={f.id}>{f.frequency}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={6}>
            <TextField
              label="Capital Payment Frequency"
              select
              fullWidth
              value={rolloverData.capital_paid_frequency_id}
              onChange={(e) => setRolloverData(prev => ({ ...prev, capital_paid_frequency_id: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              helperText={`Current: ${contract?.capital_paid_frequency || 'At Maturity'}`}
            >
              {frequencies.map((f) => (
                <MenuItem key={f.id} value={f.id}>{f.frequency}</MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Calculation Profile */}
          <Grid size={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Calculation Profile
            </Typography>
          </Grid>
          <Grid size={12}>
            <TextField
              select
              label="Calculation Profile"
              fullWidth
              value={rolloverData.calculation_profile_id}
              onChange={(e) => setRolloverData(prev => ({ ...prev, calculation_profile_id: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              helperText={`Current: ${contract?.calculation_profile_name || '-'}`}
            >
              {calculationProfiles.filter(p => p.active !== false).map((profile) => (
                <MenuItem key={profile.id} value={profile.id}>
                  {profile.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {selectedProfile && (
            <Grid size={12}>
              <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {selectedProfile.description}
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', backgroundColor: '#fff', p: 1, borderRadius: 1 }}>
                  {selectedProfile.formula_display}
                </Typography>
                <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip label={selectedProfile.interest_method} size="small" variant="outlined" />
                  <Chip label={selectedProfile.day_count_convention} size="small" variant="outlined" />
                  {selectedProfile.compounding_frequency && selectedProfile.interest_method !== 'Simple' && (
                    <Chip label={`Compounding: ${selectedProfile.compounding_frequency}x/year`} size="small" variant="outlined" />
                  )}
                </Box>
              </Paper>
            </Grid>
          )}

          {/* Per-Investor Capital Allocation */}
          <Grid size={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Capital Allocation {investorAllocations.length > 1 ? `(${investorAllocations.length} Investors)` : ''}
            </Typography>
          </Grid>
          {loadingInvestors ? (
            <Grid sx={{ textAlign: 'center', py: 2 }} size={12}>
              <CircularProgress size={24} />
            </Grid>
          ) : (
            <Grid size={12}>
              <TableContainer sx={{ maxHeight: 300 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ '& th': { backgroundColor: '#f5f5f5', fontWeight: 'bold', fontSize: '0.75rem' } }}>
                      <TableCell>Investor</TableCell>
                      <TableCell align="right">Original Capital</TableCell>
                      <TableCell align="right">New Funds</TableCell>
                      <TableCell align="right">Amount to Roll</TableCell>
                      <TableCell align="right">Paid Out</TableCell>
                      <TableCell align="right">Roll to Separate Contract</TableCell>
                      <TableCell align="center">WHT %</TableCell>
                      <TableCell align="center">Gross-Up</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {investorAllocations.map((inv) => {
                      const invSum = (parseFloat(inv.rolled_amount) || 0) + (parseFloat(inv.payout_amount) || 0) + (parseFloat(inv.unallocated_amount) || 0);
                      const invOver = invSum - inv.original_capital > 0.01;
                      return (
                        <TableRow key={inv.investor_id} sx={invOver ? { backgroundColor: '#fff3e0' } : undefined}>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{inv.investor_name}</TableCell>
                          <TableCell align="right">{currencyCode} {formatWithSpaces(inv.original_capital)}</TableCell>
                          <TableCell align="right" sx={{ p: 0.5 }}>
                            <CurrencyInput
                              value={inv.new_funds_amount}
                              onChange={(val) => handleAllocationChange(inv.investor_id, 'new_funds_amount', val)}
                              size="small"
                              sx={{ width: 130 }}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ p: 0.5 }}>
                            <CurrencyInput
                              value={inv.rolled_amount}
                              onChange={(val) => handleAllocationChange(inv.investor_id, 'rolled_amount', val)}
                              size="small"
                              sx={{ width: 130 }}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ p: 0.5 }}>
                            <CurrencyInput
                              value={inv.payout_amount}
                              onChange={(val) => handleAllocationChange(inv.investor_id, 'payout_amount', val)}
                              size="small"
                              sx={{ width: 130 }}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{
                            color: (inv.unallocated_amount || 0) > 0 ? 'warning.main' : undefined,
                            fontWeight: (inv.unallocated_amount || 0) > 0 ? 'bold' : undefined,
                          }}>
                            {currencyCode} {formatWithSpaces(inv.unallocated_amount)}
                          </TableCell>
                          <TableCell align="center">{inv.wht_rate}%</TableCell>
                          <TableCell align="center">
                            <Chip label={inv.wht_gross_up ? 'Yes' : 'No'} size="small"
                              color={inv.wht_gross_up ? 'success' : 'default'} variant="outlined" />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {investorAllocations.length > 1 && (
                      <TableRow sx={{ '& td': { fontWeight: 'bold', backgroundColor: '#f5f5f5', borderTop: '2px solid #ccc' } }}>
                        <TableCell>{investorAllocations.length} Investors</TableCell>
                        <TableCell align="right">{currencyCode} {formatWithSpaces(allocationTotals.original)}</TableCell>
                        <TableCell align="right">{currencyCode} {formatWithSpaces(allocationTotals.newFunds)}</TableCell>
                        <TableCell align="right">{currencyCode} {formatWithSpaces(allocationTotals.rolled)}</TableCell>
                        <TableCell align="right">{currencyCode} {formatWithSpaces(allocationTotals.payout)}</TableCell>
                        <TableCell align="right">{currencyCode} {formatWithSpaces(allocationTotals.unallocated)}</TableCell>
                        <TableCell />
                        <TableCell />
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {hasOverAllocation && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  Allocation exceeds original capital. Amount to Roll + Paid Out + Roll to Separate Contract must equal Original Capital.
                </Typography>
              )}
            </Grid>
          )}
          {/* New Contract Capital summary */}
          <Grid size={6}>
            <TextField
              label="New Contract Capital (Rolled + New Funds)"
              fullWidth
              value={`${currencyCode} ${formatWithSpaces(allocationTotals.rolled + allocationTotals.newFunds)}`}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              variant="filled"
              sx={{
                '& .MuiFilledInput-root': {
                  backgroundColor: '#e3f2fd',
                  fontWeight: 'bold',
                }
              }}
            />
          </Grid>
          <Grid size={6}>
            <TextField
              label="Total Paid Out"
              fullWidth
              value={`${currencyCode} ${formatWithSpaces(allocationTotals.payout)}`}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              variant="filled"
            />
          </Grid>
        </Grid>
      </ContractActionDialogBase>
      {/* Rollover Preview Modal */}
      <ScheduleImpactPreviewModal
        open={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        title="Rollover Impact Preview"
        changeTypeLabel="Rollover"
        changeContext={[
          { label: 'New maturity', value: rolloverData.new_maturity_date },
          ...(rolloverData.new_interest_rate
            ? [{ label: 'Rate', value: `${rolloverData.new_interest_rate}%` }]
            : []),
          { label: 'Rolled', value: `${currencyCode} ${formatWithSpaces(allocationTotals.rolled)}` },
          ...(allocationTotals.newFunds > 0
            ? [{ label: 'New funds', value: `${currencyCode} ${formatWithSpaces(allocationTotals.newFunds)}` }]
            : []),
          ...(allocationTotals.payout > 0
            ? [{ label: 'Payout', value: `${currencyCode} ${formatWithSpaces(allocationTotals.payout)}` }]
            : []),
        ]}
        previewData={previewData}
        contract={contract}
        contractOverrides={{
          capital_amount: allocationTotals.rolled + allocationTotals.newFunds,
          deposit_date: contract?.maturity_date,
          ...(rolloverData.new_maturity_date ? { maturity_date: rolloverData.new_maturity_date } : {}),
          ...(rolloverData.new_interest_rate ? {
            interest_rate: parseFloat(rolloverData.new_interest_rate),
            initial_interest_rate: parseFloat(rolloverData.new_interest_rate),
          } : {}),
          ...(rolloverData.investment_term_value ? { investment_term_value: parseInt(rolloverData.investment_term_value) } : {}),
          ...(rolloverData.investment_term ? { investment_term: rolloverData.investment_term } : {}),
          ...(rolloverData.interest_paid_frequency_id ? {
            interest_paid_frequency: frequencies.find(f => f.id === parseInt(rolloverData.interest_paid_frequency_id))?.frequency,
          } : {}),
          ...(rolloverData.capital_paid_frequency_id ? {
            capital_paid_frequency: frequencies.find(f => f.id === parseInt(rolloverData.capital_paid_frequency_id))?.frequency,
          } : {}),
          ...(selectedProfile ? {
            calculation_profile_name: selectedProfile.name,
            calculation_profile_interest_method: selectedProfile.interest_method,
            calculation_profile_formula: selectedProfile.formula_display,
            calculation_profile_compounding_frequency: selectedProfile.compounding_frequency,
            day_count_basis: selectedProfile.day_count_convention?.includes('360') ? '360' : '365',
          } : {}),
        }}
      />
    </>
  );
};

export default RolloverDialog;
